import api from '../services/api';
import { getSiteId } from '../siteId/siteId';

const ORDERS_V2_URL = '/orders/v2';

/*
 * Order shipments hang off their own path rather than /orders/v2, but they are order-scoped data
 * reached by order id and guarded by the same standardOrderAccessValidator, so they belong to this
 * domain rather than to a service of their own.
 */

const ORDER_SHIPMENTS_V2_URL = '/orderShipments/v2';

/*
 * The storefront slice of OrderV2RestController, OrderItemV2RestController#findOrderItemsForOrders,
 * OrderLogEntryV2RestController and OrderShipmentV2RestController#getShipmentsByOrderId - the methods
 * carrying @CollaterateApi(Realm.STOREFRONT). The rest of
 * /orders/v2 (ledger, adjustments, prepress, add-item) is supplier/site admin only and is not reachable
 * from a storefront session, so it is deliberately absent here.
 *
 * SiteAndSupplierResolveFilter resolves the site from the siteId request param before it falls back to
 * the request domain, and every storefront method on these controllers declares siteId required, so it
 * is sent on all of them rather than relying on the domain lookup.
 */

const orderService = {
    getAll,
    search,
    getById,
    getItems,
    getShipments,
    getPromotion,
    getLoyaltyCoupon,
    getBillingAddress,
    getPaymentInfo,
    getTermsAdjustments,
    getActivityLog,
    addActivityLogEntry,
    patchOrder,
};

export default orderService;

function getAll (page) {
    const requestConfig = {
        url: ORDERS_V2_URL,
        method: 'GET',
        params: {
            siteId: getSiteId(),
            page,
        },
    };

    return api(requestConfig);
}

function search (criteria, page) {
    const requestConfig = {
        url: `${ORDERS_V2_URL}/search`,
        method: 'POST',
        params: {
            siteId: getSiteId(),
            page,
        },
        data: criteria,
    };

    return api(requestConfig);
}

function getById (orderId) {
    const requestConfig = {
        url: `${ORDERS_V2_URL}/${orderId}`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
        },
    };

    return api(requestConfig);
}

/*
 * The path variable is a comma separated list, not a single id - one call can fetch the items for a
 * whole page of orders. OrderItemV2RestController#ORDER_IDS_LIMIT caps it at 100.
 */

function getItems (orderIds, page) {
    const requestConfig = {
        url: `${ORDERS_V2_URL}/${orderIds.join(',')}/items`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
            page,
        },
    };

    return api(requestConfig);
}

/*
 * The order id is a query param here, not a path variable - this endpoint lists the shipments belonging
 * to one order rather than addressing a shipment directly.
 */

function getShipments (orderId) {
    const requestConfig = {
        url: ORDER_SHIPMENTS_V2_URL,
        method: 'GET',
        params: {
            siteId: getSiteId(),
            orderId,
        },
    };

    return api(requestConfig);
}

/*
 * OrdersProvider#findPromotionByOrderId raises RESOURCES_NOT_FOUND - a 404 - when the order simply
 * carries no promotion, which is the common case, so the absence resolves to null rather than
 * rejecting. findLoyaltyCouponByOrderId does the same, and also treats an inactive coupon as absent.
 */

function getPromotion (orderId) {
    const requestConfig = {
        url: `${ORDERS_V2_URL}/${orderId}/promotion`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
        },
        resolves: [{
            status: 404,
            data: null,
        }],
    };

    return api(requestConfig);
}

function getLoyaltyCoupon (orderId) {
    const requestConfig = {
        url: `${ORDERS_V2_URL}/${orderId}/loyaltyCoupon`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
        },
        resolves: [{
            status: 404,
            data: null,
        }],
    };

    return api(requestConfig);
}

function getBillingAddress (orderId) {
    const requestConfig = {
        url: `${ORDERS_V2_URL}/${orderId}/billingAddress`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
        },
    };

    return api(requestConfig);
}

/*
 * 404 here means the order was not paid by card - terms, PO and zero-total orders have no
 * OrderCcPaymentInfo row at all - which is a normal state, not a failure.
 */

function getPaymentInfo (orderId) {
    const requestConfig = {
        url: `${ORDERS_V2_URL}/${orderId}/ccPaymentInfo`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
        },
        resolves: [{
            status: 404,
            data: null,
        }],
    };

    return api(requestConfig);
}

function getTermsAdjustments (orderId) {
    const requestConfig = {
        url: `${ORDERS_V2_URL}/${orderId}/termsAdjustments`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
        },
    };

    return api(requestConfig);
}

function getActivityLog (orderId) {
    const requestConfig = {
        url: `${ORDERS_V2_URL}/${orderId}/activityLog`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
        },
    };

    return api(requestConfig);
}

function addActivityLogEntry (orderId, entry) {
    const requestConfig = {
        url: `${ORDERS_V2_URL}/${orderId}/activityLog`,
        method: 'POST',
        params: {
            siteId: getSiteId(),
        },
        data: entry,
    };

    return api(requestConfig);
}

/*
 * Unlike PATCH /projects/v2/{id} this is not guarded by an optimistic lock on updated_on, so it needs
 * no serialQueue - OrdersProvider#patchOrder loads, sets and saves. projectName is the only field
 * OrderPatchRequestDTO exposes to the storefront realm; anything else in the body is ignored.
 */

function patchOrder (orderId, patch) {
    const requestConfig = {
        url: `${ORDERS_V2_URL}/${orderId}`,
        method: 'PATCH',
        params: {
            siteId: getSiteId(),
        },
        data: patch,
    };

    return api(requestConfig);
}
